import { useEffect, useRef, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { PencilSquareIcon, XMarkIcon } from "@heroicons/react/24/outline";
import AiSpinner from "../SmartNoteModal/AiSpinner";
import NoteTagEditor from "../SmartNoteModal/NoteTagEditor";

// A focused composer for writing a note directly against the account (the header's "Add note"), separate
// from the AI chat. Just a note body and an optional "notify a colleague" tag editor — the same note save
// the chat uses, so the result lands on the timeline identically. Elevated above the (already modal) account
// view. Saving locks the form; the parent closes it on success so the Activity reveal plays as it closes.
export default function AddNoteModal({ isOpen, customer, onSave, onClose, saving = false }) {
  const [text, setText] = useState("");
  const [tags, setTags] = useState([]);
  const textareaRef = useRef(null);

  // Start clean each time it opens.
  useEffect(() => {
    if (isOpen) {
      setText("");
      setTags([]);
    }
  }, [isOpen]);

  const canSave = text.trim().length > 0 && !saving;
  const submit = () => {
    if (!canSave) return;
    onSave({ content: text.trim(), mentions: tags });
  };

  const onKeyDown = (e) => {
    // ⌘/Ctrl + Enter saves without reaching for the mouse — the note is often a quick aside.
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const requestClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={isOpen} onClose={requestClose} className="relative z-[70]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-900/40 backdrop-blur-[1px] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150"
      />
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl transition-all data-[closed]:translate-y-3 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5">
              <div className="flex items-center gap-2.5">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-zinc-100 text-zinc-600">
                  <PencilSquareIcon aria-hidden="true" className="size-5" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-zinc-900">Add note</h2>
                  {customer?.name && (
                    <p className="truncate text-xs text-zinc-400">{customer.name}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={requestClose}
                disabled={saving}
                aria-label="Close"
                className="-mr-1 rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>

            <div className="px-5 pt-4">
              <div className="rounded-xl border border-zinc-300 bg-white p-3 shadow-sm transition focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-primary">
                <textarea
                  ref={textareaRef}
                  rows={8}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={text}
                  disabled={saving}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={`Write a note about ${customer?.name ?? "this account"}…`}
                  className="w-full resize-none bg-transparent text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none disabled:text-zinc-400"
                />
                <div className="mt-2">
                  <NoteTagEditor users={tags} onChange={setTags} />
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 border-t border-zinc-100 px-5 py-3">
              <span className="hidden text-xs text-zinc-400 sm:inline">
                <kbd className="font-sans">⌘</kbd>
                <kbd className="font-sans">↵</kbd> to save
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={saving}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSave}
                  className="inline-flex min-w-[6rem] items-center justify-center gap-1.5 rounded-lg bg-primary-500 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-600 disabled:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                  {saving ? <AiSpinner size={16} /> : "Add note"}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
